import React, { useEffect, useState } from 'react';
import { usePagination } from '../../../hooks';
import { getAllUsers, popup, updateCSMStatus } from '../../../utils';
import { Modal } from 'react-bootstrap';
import { setSelectedHiringClients } from '../../../redux';
import { useDispatch } from 'react-redux';
import { DEACTIVATED_CSM_COLUMNS } from '../../../constants';
import { isEmpty } from 'lodash';
import { Table } from '..';
import InputWithSearch from '../common/InputWithSearch';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../../constants/queryKey';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

const ActivateUserModal = ({
    showModal,
    setShowModal,
    forRoles = [],
    searchBarPlaceholder = 'Search...',
    pageName = '',
}) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [deactivatedCSM, setDeactivatedCSM] = useState({ data: [], count: 0 });
    const [search, setSearch] = useState('');
    const pagination = usePagination(deactivatedCSM?.data.length, deactivatedCSM?.count);
    const { selectedHiringClients } = useSelector((s) => s.accountArray);

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const { data, isError, error, isFetching } = useQuery({
        queryKey: [
            QUERY_KEYS.INACTIVE_USERS,
            pagination.limit,
            pagination.offset,
            debouncedSearch,
            forRoles,
        ],
        queryFn: () =>
            getAllUsers({
                offset: pagination.offset,
                limit: pagination.limit,
                search: search,
                filter: { status: false },
                role: forRoles,
            }),
        select: (data) => ({
            data: data?.data || [],
            count: data?.count || 0,
        }),
    });
    if (isError) popup('error', error.message);

    useEffect(() => {
        if (data) {
            setDeactivatedCSM(data);
        }
    }, [data]);

    const userActivateMutation = useMutation({
        mutationFn: async () => {
            const res = await updateCSMStatus({
                status: 1,
                ids: selectedHiringClients,
            });
            return res;
        },
        onSuccess: (data) => {
            popup('success', data.message);
            dispatch(setSelectedHiringClients([]));
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.USERS],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.INACTIVE_USERS],
                exact: false,
            });
            setShowModal(false);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    return (
        <Modal
            show={showModal}
            onHide={() => {
                setShowModal(false);
                dispatch(setSelectedHiringClients([]));
            }}
            size='lg'
            dialogClassName='overflow-hidden'
            fullscreen='md-down'
        >
            <Modal.Header closeButton>
                <Modal.Title className='me-3' style={{ fontSize: '18px' }}>
                    {pageName}
                </Modal.Title>
                <InputWithSearch
                    search={search}
                    setSearch={setSearch}
                    placeholder={searchBarPlaceholder}
                    className={'w-50'}
                />
            </Modal.Header>
            <Modal.Body className='position-relative' style={{ minHeight: '42.75rem' }}>
                {!isEmpty(deactivatedCSM) && (
                    <Table
                        columns={DEACTIVATED_CSM_COLUMNS}
                        data={deactivatedCSM?.data}
                        pagination={pagination}
                        tableStyle={{
                            height: '36.25rem',
                            overflow: 'auto',
                        }}
                        stickyColumns={['Name']}
                        pageName={pageName}
                        isLoading={isFetching}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className='d-flex w-100 align-items-center justify-content-between modal-btn'>
                    <span
                        style={{ color: '#738da6', fontSize: '14px' }}
                    >{`${selectedHiringClients.length}/${deactivatedCSM?.count} Selected`}</span>
                    <button
                        type='submit'
                        onClick={() => userActivateMutation.mutate()}
                        className='ssc-primary-green-btn py-2 w-auto disabled-hover rounded-2'
                        disabled={
                            selectedHiringClients.length === 0 || userActivateMutation.isPending
                        }
                    >
                        Activate
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ActivateUserModal;
